/**
 * RUT chileno: formato y digito verificador (CHK-04).
 *
 * Se valida en el navegador y no solo al facturar porque Random rechaza un RUT
 * invalido con un error de datos, y ese rechazo llegaria despues de cobrar. El
 * modulo 11 atrapa el tipeo antes: es aritmetica, no una consulta al SII, asi
 * que no dice si el RUT existe ni de quien es —solo que esta bien escrito—.
 *
 * Funciones puras, sin React y sin DOM.
 */

/** Deja solo digitos y la K final, en mayuscula. */
function limpiar(rut: string): string {
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
}

/**
 * Digito verificador de un cuerpo numerico, por modulo 11.
 *
 * Se recorre de derecha a izquierda multiplicando por la serie 2..7 ciclica.
 */
function digitoVerificador(cuerpo: string): string {
  let suma = 0;
  let multiplicador = 2;

  for (let posicion = cuerpo.length - 1; posicion >= 0; posicion -= 1) {
    suma += Number(cuerpo[posicion]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }

  const resto = 11 - (suma % 11);
  if (resto === 11) return '0';
  if (resto === 10) return 'K';
  return String(resto);
}

export function rutEsValido(rut: string): boolean {
  const limpio = limpiar(rut);
  // Menos de siete digitos no es un RUT: son 7 u 8 mas el verificador.
  if (limpio.length < 8 || limpio.length > 9) return false;

  const cuerpo = limpio.slice(0, -1);
  const verificador = limpio.slice(-1);

  if (!/^\d+$/.test(cuerpo)) return false;

  return digitoVerificador(cuerpo) === verificador;
}

/**
 * Formato chileno con puntos y guion: 12.345.678-5.
 *
 * Tolera un RUT a medio escribir (`1234` → `123-4`): es lo que se ve mientras
 * se tipea.
 */
export function formatearRut(rut: string): string {
  const limpio = limpiar(rut);
  if (limpio.length === 0) return '';
  if (limpio.length === 1) return limpio;

  const cuerpo = limpio.slice(0, -1);
  const verificador = limpio.slice(-1);
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${conPuntos}-${verificador}`;
}

/** Cuerpo de ocho digitos mas el verificador. */
const LARGO_MAXIMO = 9;

function tieneSeparadores(texto: string): boolean {
  return /[.-]/.test(texto);
}

/**
 * El valor del campo despues de una tecla.
 *
 * Quien escribe solo los numeros ve aparecer los puntos y el guion solos.
 * Quien escribe un punto o un guion lo esta formateando a su manera y no se le
 * toca nada: el servidor lo deja igual al guardar (sin puntos, con guion).
 */
export function rutAlEscribir(anterior: string, nuevo: string): string {
  // Ya venia escrito a mano: se respeta hasta que se vacie el campo.
  const anteriorAutomatico = anterior === '' || anterior === formatearRut(anterior);
  if (!anteriorAutomatico) return nuevo;

  const agregado = nuevo.length > anterior.length;
  if (agregado && nuevo.startsWith(anterior)) {
    const tecleado = nuevo.slice(anterior.length);
    // Escribio (o pego) un punto o un guion: desde aca manda la persona. Lo
    // que llevaba eran numeros con el formato puesto por nosotros, asi que se
    // le devuelven sus numeros tal como los tipeo, mas lo que acaba de poner.
    if (tieneSeparadores(tecleado)) return limpiar(anterior) + tecleado;
  }

  let limpio = limpiar(nuevo);
  // Borro un punto o el guion que pusimos nosotros: lo que quiso borrar es el
  // caracter que estaba al lado, si no la tecla pareceria no hacer nada.
  if (!agregado && limpio === limpiar(anterior)) limpio = limpio.slice(0, -1);

  return formatearRut(limpio.slice(0, LARGO_MAXIMO));
}
